import "./TestWebshop.scss";
import "@/apps/partner/components/IconTile/IconTilePartner.scss";

import clsx from "clsx";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { PersonalDetailsFormFields } from "@/apps/partner/components/PersonalDetails/PersonalDetailsFormFields";
import { useCheckPartnerCustomer } from "@/apps/partner/hooks/useCheckPartnerCustomer";
import { ESim, Info } from "@/assets/icons/icons";
import SimCard from "@/assets/icons/SimCard.svg?react";
import { Button } from "@/components/Interface/Button/Button";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { ListBox } from "@/components/Interface/FormFields/ListBox/ListBox";
import { RadioButton } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import {
  getMobileAbos,
  getSortedMobileAbosMonthly,
  getSortedMobileAbosYearly,
} from "@/components/Onboarding/Steps/RateSelection/rateSelection.utils";
import { getHeader } from "@/components/Portal/Cockpit/DeviceManagement/Roaming/roamingUtils";
import { SCHWEIZ } from "@/constants/countries";
import { useHandleError } from "@/hooks/useHandleError";
import { useGetDevices, useGetOptions, usePrepareBasket } from "@/services/api";
import type { Device, PrepareBasketResponse } from "@/services/model";
import { SalesPartnerCreateCustomerLanguageEnum } from "@/services/model";
import { accessTokenAtom, setAuthAtom } from "@/utils/atoms";
import { BACKEND_EMAIL_REGEX, mobileAbos, ServiceIDs } from "@/utils/constants";
import { DeviceName, deviceNameToValueMap } from "@/utils/deviceUtils";
import {
  isFlatHomeTariff,
  type TransformedProduct,
  transformProducts,
} from "@/utils/tariffUtils";

import { partnerSalesLogin } from "./partnerSalesLogin";
import type { TestWebshopFormType } from "./testWebshopTypes";
import { transformFormDataToPrepareBasketRequest } from "./transformPrepareBasketRequest";

type PartnerLoginForm = {
  email: string;
  password: string;
};

const SMARTPHONE_PRODUCT_ID = 131001;

const FLAT_HOME_PRODUCT_IDS = [
  ServiceIDs.FlatHome4G,
  ServiceIDs.FlatHome5G,
] as const;

const flatHomeFallbackOption = (
  id: (typeof FLAT_HOME_PRODUCT_IDS)[number],
): TransformedProduct => ({
  id,
  baseDisplayValue: `Flat Home ${id === ServiceIDs.FlatHome4G ? "4G" : "5G"}`,
  chargeFlat: "",
  isVoiceProduct: false,
  data: id,
  voice: id,
});

export const TestWebshop = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const accessToken = useAtomValue(accessTokenAtom);
  const setAuth = useSetAtom(setAuthAtom);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [prepareBasketResult, setPrepareBasketResult] =
    useState<PrepareBasketResponse | null>(null);
  const [lastRequestJson, setLastRequestJson] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<Device | undefined>();

  const partnerLoginForm = useForm<PartnerLoginForm>({
    defaultValues: {
      email: "reseller@ambient.digital",
      password: "Test123!",
    },
  });

  const handlePartnerLogin = async ({ email, password }: PartnerLoginForm) => {
    setLoginError(null);
    setIsLoggingIn(true);
    try {
      const tokens = await partnerSalesLogin({ email, password });
      setAuth({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        isAnonymous: false,
      });
    } catch {
      setLoginError("Sales partner login failed. Check email and password.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const formMethods = useForm<TestWebshopFormType>({
    mode: "onSubmit",
    defaultValues: {
      simType: undefined,
      device: "",
      simAlias: "",
      productId: undefined,
      onp: "false",
      mail: "",
      isExistingCustomer: false,
      customer: {
        salutationEnum: "1",
        languageEnum: SalesPartnerCreateCustomerLanguageEnum.DE,
        firstName: "",
        lastName: "",
        street: "",
        houseNumber: "",
        postalDeliveryNotes: "",
        zip: "",
        city: "",
        countryCodeAlpha2: SCHWEIZ,
        contactPhoneNumber: "",
        birthday: "",
      },
    },
  });

  const {
    register,
    watch,
    setValue,
    unregister,
    formState: { errors },
    handleSubmit,
  } = formMethods;

  const simType = watch("simType");
  const deviceDescription = watch("device");
  const emailValue = watch("mail") || "";
  const { isExistingCustomer, isChecking } =
    useCheckPartnerCustomer(emailValue);

  useEffect(() => {
    setValue("isExistingCustomer", isExistingCustomer);
    if (isExistingCustomer) {
      unregister("customer");
    }
  }, [isExistingCustomer, setValue, unregister]);

  const { data: devices, isFetching: isLoadingDevices } = useGetDevices({
    query: {
      enabled: !!accessToken,
      cacheTime: 24 * 60 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  });

  useEffect(() => {
    if (devices?.length && !deviceDescription) {
      const smartphone = devices.find((d) =>
        d.description?.toLowerCase().includes("smartphone"),
      );
      if (smartphone) {
        setSelectedDevice(smartphone);
        setValue("device", smartphone.description ?? "");
      }
    }
  }, [devices, deviceDescription, setValue]);

  useEffect(() => {
    if (selectedDevice) {
      setValue("device", selectedDevice.description ?? "", {
        shouldValidate: false,
      });
    }
  }, [selectedDevice, setValue]);

  const { data: transformedProducts, isFetching: isLoadingProducts } =
    useGetOptions(SMARTPHONE_PRODUCT_ID, undefined, {
      query: {
        enabled: !!accessToken && !!deviceDescription,
        refetchOnWindowFocus: false,
        select: (data) => transformProducts(data),
      },
    });

  const { mobileAboOptions, flatHomeOptions, dataTariffOptions } =
    useMemo(() => {
      const byId = transformedProducts?.processedProductsById;
      if (!byId) {
        return {
          mobileAboOptions: [],
          flatHomeOptions: FLAT_HOME_PRODUCT_IDS.map(flatHomeFallbackOption),
          dataTariffOptions: [],
        };
      }

      const mobileAboOptions = [
        ...getSortedMobileAbosMonthly(getMobileAbos(byId)),
        ...getSortedMobileAbosYearly(getMobileAbos(byId)),
      ];
      const flatHomeFromApi = Object.values(byId).filter(
        (p) => p.id && isFlatHomeTariff(p.id),
      );
      const flatHomeOptions =
        flatHomeFromApi.length > 0
          ? flatHomeFromApi.sort(
              (a, b) =>
                FLAT_HOME_PRODUCT_IDS.indexOf(
                  a.id as (typeof FLAT_HOME_PRODUCT_IDS)[number],
                ) -
                FLAT_HOME_PRODUCT_IDS.indexOf(
                  b.id as (typeof FLAT_HOME_PRODUCT_IDS)[number],
                ),
            )
          : FLAT_HOME_PRODUCT_IDS.map(flatHomeFallbackOption);
      const dataTariffOptions = Object.values(byId).filter(
        (p) =>
          p.id &&
          !p.isVoiceProduct &&
          !mobileAbos.includes(`${p.id}`) &&
          !isFlatHomeTariff(p.id),
      );

      return { mobileAboOptions, flatHomeOptions, dataTariffOptions };
    }, [transformedProducts]);

  const { mutate: prepareBasket, isLoading } = usePrepareBasket({
    mutation: {
      onSuccess: (data) => setPrepareBasketResult(data),
      onError: (error) => handleError(error),
    },
  });

  const onSubmit = (data: TestWebshopFormType) => {
    setPrepareBasketResult(null);
    setLastRequestJson(null);

    if (!data.simType) {
      formMethods.setError("simType", {
        type: "required",
        message: "Select SIM type",
      });

      return;
    }

    try {
      const payload = transformFormDataToPrepareBasketRequest(
        data,
        getHeader(),
      );
      setLastRequestJson(JSON.stringify(payload, null, 2));
      prepareBasket({ data: payload });
    } catch (error) {
      handleError(error);
    }
  };

  if (!accessToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-4 py-10">
        <div className="test-webshop-tile active w-full max-w-md p-8">
          <h1 className="mb-2 text-2xl font-semibold text-primary-100">
            Test Webshop – Sales partner login
          </h1>
          <p className="mb-6 text-secondary-100">
            prepareBasket requires a sales partner session (pos_login).
          </p>
          <form
            onSubmit={partnerLoginForm.handleSubmit(handlePartnerLogin)}
            className="flex flex-col gap-4"
          >
            <FormField
              id="partner-login-email"
              label="Email"
              name="email"
              type="email"
              errors={partnerLoginForm.formState.errors}
              register={partnerLoginForm.register}
              options={{ required: true }}
            />
            <FormField
              id="partner-login-password"
              label="Password"
              name="password"
              type="password"
              errors={partnerLoginForm.formState.errors}
              register={partnerLoginForm.register}
              options={{ required: true }}
            />
            {loginError && (
              <p className="text-sm text-error-100">{loginError}</p>
            )}
            <Button
              type="submit"
              className="accent w-full py-3"
              disabled={isLoggingIn}
              dataTestid="test-webshop-partner-login"
            >
              {isLoggingIn ? "Logging in…" : "Log in as sales partner"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-4 py-10 md:px-8">
      <div id="test-webshop-wrapper" className="mx-auto max-w-3xl">
        <div className="test-webshop-tile active p-6 md:p-10">
          <h1 className="mb-2 text-2xl font-semibold text-primary-100">
            Test Webshop – prepareBasket
          </h1>
          <p className="mb-10 text-secondary-100">
            Sales partner test page for POST /baskets/prepareBasket
          </p>

          <FormProvider {...formMethods}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-10"
            >
              <section>
                <h2 className="mb-4 text-lg font-semibold text-primary-100">
                  SIM type
                </h2>
                {errors.simType && (
                  <p className="mb-2 text-sm text-error-100">
                    {String(errors.simType.message)}
                  </p>
                )}
                <div className="flex flex-col gap-4 md:flex-row">
                  <button
                    type="button"
                    aria-label={t("Tile.label.activateSim")}
                    data-testid="triplesim-tile"
                    className={clsx(
                      "icon-tile-partner",
                      simType === "TRIPLESIM" && "selected",
                    )}
                    onClick={() =>
                      setValue("simType", "TRIPLESIM", {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  >
                    <div className="icon-container-partner">
                      <SimCard />
                    </div>
                    <p className="label-container-partner">
                      {t("Tile.label.activateSim")}
                    </p>
                  </button>
                  <button
                    type="button"
                    aria-label={t("Tile.label.activateESim")}
                    data-testid="esim-tile"
                    className={clsx(
                      "icon-tile-partner",
                      simType === "ESIM" && "selected",
                    )}
                    onClick={() =>
                      setValue("simType", "ESIM", {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  >
                    <div className="icon-container-partner">
                      <ESim />
                    </div>
                    <p className="label-container-partner">
                      {t("Tile.label.activateESim")}
                    </p>
                  </button>
                </div>
                {simType && (
                  <p className="mt-2 text-sm text-secondary-100">
                    Selected: {simType}
                  </p>
                )}
                <p className="mt-1 text-xs text-secondary-100">
                  prepareBasket does not send iccid/puk (optional in API).
                  TRIPLESIM iccid validation on the server is a known backend
                  issue.
                </p>
              </section>

              <section>
                <h2 className="mb-4 text-lg font-semibold text-primary-100">
                  Device
                </h2>
                <ListBox<Device>
                  by="id"
                  selected={selectedDevice}
                  setSelected={setSelectedDevice}
                  dataTestId="device-type"
                  availableValues={devices ?? []}
                  displayValue={(device) =>
                    t(
                      `Common.devices.${
                        deviceNameToValueMap[device.description as DeviceName]
                      }`,
                    )
                  }
                  placeholder={t(
                    "portal:layout.tiles.upsellSim.selectDevicePlaceholder",
                  )}
                  disabled={isLoadingDevices}
                />
                <div className="mt-4">
                  <FormField
                    id="sim-alias"
                    label={t("portal:common.device.attributes.deviceName")}
                    name="simAlias"
                    errors={errors}
                    register={register}
                    type="text"
                    options={{
                      required: t("translation:label.validation.required"),
                    }}
                  />
                </div>
              </section>

              <section>
                <h2 className="mb-4 text-lg font-semibold text-primary-100">
                  Product
                </h2>
                {isLoadingProducts ? (
                  <LoadingSpinner />
                ) : (
                  <select
                    className="w-full rounded border border-secondary-100 p-3"
                    {...register("productId", {
                      required: true,
                      valueAsNumber: true,
                    })}
                  >
                    <option value="">Select product…</option>
                    {mobileAboOptions.length > 0 && (
                      <optgroup label="Mobile Abo">
                        {mobileAboOptions.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.baseDisplayValue} ({product.chargeFlat})
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {flatHomeOptions.length > 0 && (
                      <optgroup label="Flat Home">
                        {flatHomeOptions.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.baseDisplayValue}
                            {product.chargeFlat
                              ? ` (${product.chargeFlat})`
                              : ""}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {dataTariffOptions.length > 0 && (
                      <optgroup label="Data tariffs">
                        {dataTariffOptions.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.baseDisplayValue} ({product.chargeFlat})
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                )}
                {errors.productId && (
                  <p className="mt-2 text-sm text-error-100">
                    {t("translation:label.validation.required")}
                  </p>
                )}
              </section>

              <section>
                <h2 className="mb-4 text-lg font-semibold text-primary-100">
                  Number porting (ONP)
                </h2>
                <div className="flex gap-6">
                  <RadioButton
                    label="No"
                    name="onp"
                    value="false"
                    errors={errors}
                    register={register}
                  />
                  <RadioButton
                    label="Yes"
                    name="onp"
                    value="true"
                    errors={errors}
                    register={register}
                  />
                </div>
              </section>

              <section>
                <h2 className="mb-4 text-lg font-semibold text-primary-100">
                  Email
                </h2>
                <FormField
                  id="test-webshop-email"
                  label={t("user-account.personalDetails.loginData.email")}
                  name="mail"
                  type="email"
                  errors={errors}
                  register={register}
                  options={{
                    required: t("translation:label.validation.required"),
                    pattern: {
                      value: BACKEND_EMAIL_REGEX,
                      message: t(
                        "user-account.personalDetails.loginData.currentEmailError",
                      ),
                    },
                  }}
                />
                {isChecking && (
                  <p className="mt-1 text-sm text-primary-100">Checking…</p>
                )}
                {isExistingCustomer && (
                  <div className="bg-highlight-100/10 mt-4 flex items-center gap-3 rounded-lg border border-highlight-100 p-4">
                    <Info className="size-6 flex-shrink-0 text-primary-100" />
                    <p className="text-base text-primary-100">
                      Existing customer – personal details skipped
                    </p>
                  </div>
                )}
              </section>

              {!isExistingCustomer && (
                <section>
                  <h2 className="mb-4 text-lg font-semibold text-primary-100">
                    Personal details
                  </h2>
                  <PersonalDetailsFormFields />
                </section>
              )}

              {lastRequestJson && (
                <section>
                  <h3 className="mb-2 font-semibold text-primary-100">
                    Last request body
                  </h3>
                  <pre className="bg-primary-5 overflow-x-auto rounded border border-secondary-100 p-4 text-xs">
                    {lastRequestJson}
                  </pre>
                </section>
              )}

              {prepareBasketResult && (
                <div
                  className="bg-primary-5 rounded-lg border border-primary-25 p-6"
                  data-testid="prepare-basket-result"
                >
                  <h3 className="mb-4 font-semibold text-primary-100">
                    prepareBasket response
                  </h3>
                  <dl className="grid gap-2 text-sm">
                    <div className="flex gap-2">
                      <dt className="font-medium text-secondary-100">
                        basketId:
                      </dt>
                      <dd>{prepareBasketResult.basketId ?? "—"}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="font-medium text-secondary-100">
                        customerId:
                      </dt>
                      <dd>{prepareBasketResult.customerId ?? "—"}</dd>
                    </div>
                  </dl>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  type="submit"
                  dataTestid="test-webshop-submit"
                  className="accent px-8 py-3"
                  disabled={isLoading}
                >
                  {isLoading ? "Loading…" : "prepareBasket"}
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};
